import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Star } from "lucide-react";
import { Product } from "@/types/product";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ProductModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

const ProductModal = ({ product, isOpen, onClose }: ProductModalProps) => {
  const renderStars = (rating: number = 0) => {
    const roundedRating = product.rating_amount > 0 ? Math.round(rating) : 0;
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < roundedRating
            ? "text-yellow-400 fill-yellow-400"
            : "text-gray-300"
        }`}
      />
    ));
  };

  // Add fallback values for potentially undefined properties
  const price = product?.price ?? 0;
  const rating = product?.average_rating ?? 0;
  const ratingAmount = product?.rating_amount ?? 0;
  const title = product?.title ?? "Untitled Product";
  const imageUrl = product?.image_url ?? "/placeholder.svg";
  const ean = product?.ean ?? "N/A";
  const slug = product?.slug ?? "N/A";
  const attributes = product?.p_attributes ?? {};

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl font-semibold">
            Product Details
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-full px-6 pb-6">
          <Card className="border-none shadow-none">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="aspect-square overflow-hidden rounded-lg">
                <img
                  src={imageUrl}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </div>
              <CardContent className="p-0 space-y-4">
                <h3 className="font-semibold text-lg mb-1">{title}</h3>
                <p className="text-sm text-gray-500">{slug}</p>
                <div className="flex items-center gap-1">
                  {renderStars(rating)}
                  <span className="text-sm text-gray-600 ml-1">
                    ({rating.toFixed(1)} - {ratingAmount} reviews)
                  </span>
                </div>
                <p className="text-2xl font-bold text-primary">
                  €{price.toFixed(2)}
                </p>
                <div className="text-sm text-gray-500">
                  <p>
                    <span className="font-medium text-gray-700">EAN:</span> {ean}
                  </p>
                </div>
                {Object.keys(attributes).length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-gray-900 mb-3">
                      Product Specifications
                    </h4>
                    <div className="grid gap-2">
                      {Object.entries(attributes).map(([key, value]) => (
                        <div
                          key={key}
                          className="grid grid-cols-2 gap-2 py-2 border-b border-gray-100"
                        >
                          <span className="text-sm font-medium text-gray-600">
                            {key}
                          </span>
                          <span className="text-sm text-gray-800">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </div>
          </Card>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ProductModal;